import { useState, useEffect, useCallback } from 'react';
import type { PortfolioItem, CoinData, Portfolio } from '../types/interfaces';

const API_BASE = 'https://api.coingecko.com/api/v3';
const PORTFOLIOS_STORAGE_KEY = 'cryptoscopePortfolios';
const DEFAULT_PORTFOLIO_ID = 'default';

export function usePortfolio() {
  const [portfolios, setPortfolios] = useState<Portfolio[]>(() => {
    const saved = localStorage.getItem(PORTFOLIOS_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // If parsing fails, try to migrate old portfolio format
        const oldPortfolio = localStorage.getItem('cryptoPortfolio');
        if (oldPortfolio) {
          try {
            const oldItems: PortfolioItem[] = JSON.parse(oldPortfolio);
            const defaultPortfolio: Portfolio = {
              id: DEFAULT_PORTFOLIO_ID,
              name: 'My Portfolio',
              items: oldItems,
              createdAt: Date.now()
            };
            // Save new format and remove old
            localStorage.setItem(PORTFOLIOS_STORAGE_KEY, JSON.stringify([defaultPortfolio]));
            localStorage.removeItem('cryptoPortfolio');
            return [defaultPortfolio];
          } catch {
            // Migration failed, start fresh
          }
        }
        return [];
      }
    }
    
    // Check for old portfolio format and migrate
    const oldPortfolio = localStorage.getItem('cryptoPortfolio');
    if (oldPortfolio) {
      try {
        const oldItems: PortfolioItem[] = JSON.parse(oldPortfolio);
        const defaultPortfolio: Portfolio = {
          id: DEFAULT_PORTFOLIO_ID,
          name: 'My Portfolio',
          items: oldItems,
          createdAt: Date.now()
        };
        // Save new format and remove old
        localStorage.setItem(PORTFOLIOS_STORAGE_KEY, JSON.stringify([defaultPortfolio]));
        localStorage.removeItem('cryptoPortfolio');
        return [defaultPortfolio];
      } catch {
        // Migration failed, start fresh
      }
    }
    
    // Initialize with default portfolio if none exist
    const defaultPortfolio: Portfolio = {
      id: DEFAULT_PORTFOLIO_ID,
      name: 'My Portfolio',
      items: [],
      createdAt: Date.now()
    };
    return [defaultPortfolio];
  });

  const [currentPortfolioId, setCurrentPortfolioId] = useState<string>(() => {
    const saved = localStorage.getItem('cryptoscopeCurrentPortfolioId');
    return saved || DEFAULT_PORTFOLIO_ID;
  });

  // Ensure current portfolio exists
  useEffect(() => {
    const currentExists = portfolios.some(p => p.id === currentPortfolioId);
    if (!currentExists && portfolios.length > 0) {
      setCurrentPortfolioId(portfolios[0].id);
    }
  }, [portfolios, currentPortfolioId]);

  // Save portfolios to localStorage
  useEffect(() => {
    localStorage.setItem(PORTFOLIOS_STORAGE_KEY, JSON.stringify(portfolios));
  }, [portfolios]);

  // Save current portfolio ID
  useEffect(() => {
    localStorage.setItem('cryptoscopeCurrentPortfolioId', currentPortfolioId);
  }, [currentPortfolioId]);

  const currentPortfolio = portfolios.find(p => p.id === currentPortfolioId) || portfolios[0];
  const portfolio = currentPortfolio?.items || [];

  const createPortfolio = useCallback((name: string): string => {
    const newPortfolio: Portfolio = {
      id: `portfolio-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: name || `Portfolio ${portfolios.length + 1}`,
      items: [],
      createdAt: Date.now()
    };
    setPortfolios(prev => [...prev, newPortfolio]);
    setCurrentPortfolioId(newPortfolio.id);
    return newPortfolio.id;
  }, [portfolios]);

  const deletePortfolio = useCallback((portfolioId: string) => {
    if (portfolios.length <= 1) {
      alert('Cannot delete the last portfolio. You must have at least one portfolio.');
      return;
    }
    if (confirm('Are you sure you want to delete this portfolio? This action cannot be undone.')) {
      setPortfolios(prev => prev.filter(p => p.id !== portfolioId));
      if (currentPortfolioId === portfolioId) {
        const remaining = portfolios.filter(p => p.id !== portfolioId);
        if (remaining.length > 0) {
          setCurrentPortfolioId(remaining[0].id);
        }
      }
    }
  }, [portfolios, currentPortfolioId]);

  const renamePortfolio = useCallback((portfolioId: string, newName: string) => {
    setPortfolios(prev => prev.map(p => 
      p.id === portfolioId ? { ...p, name: newName } : p
    ));
  }, []);

  const switchPortfolio = useCallback((portfolioId: string) => {
    if (portfolios.some(p => p.id === portfolioId)) {
      setCurrentPortfolioId(portfolioId);
    }
  }, [portfolios]);

  const updatePortfolioItems = useCallback((portfolioId: string, items: PortfolioItem[]) => {
    setPortfolios(prev => prev.map(p => 
      p.id === portfolioId ? { ...p, items } : p
    ));
  }, []);

  const refreshPortfolio = useCallback(async () => {
    if (portfolio.length === 0) return;

    try {
      const coinIds = portfolio.map(item => item.id).join(',');
      const response = await fetch(`${API_BASE}/simple/price?ids=${coinIds}&vs_currencies=usd&include_24hr_change=true`);
      const priceData = await response.json();

      const updatedItems = portfolio.map(item => ({
        ...item,
        currentPrice: priceData[item.id]?.usd || item.currentPrice,
        change24h: priceData[item.id]?.usd_24h_change || 0
      }));

      updatePortfolioItems(currentPortfolioId, updatedItems);
    } catch (error) {
      console.error('Error refreshing portfolio:', error);
    }
  }, [portfolio, currentPortfolioId, updatePortfolioItems]);

  const addCoin = useCallback(async (coinId: string, quantity: number, purchasePrice: number | null, cryptoData: CoinData[]) => {
    // Request payment for each coin added (500 satoshis per coin)
    try {
      if (window.bsvPayments && typeof window.bsvPayments.getPaymentAmount === 'function' && typeof window.bsvPayments.requestPayment === 'function') {
        try {
          const amount = window.bsvPayments.getPaymentAmount('ADD_TO_PORTFOLIO');
          const paymentResult = await window.bsvPayments.requestPayment('ADD_TO_PORTFOLIO', amount);
          // If payment was cancelled or failed, don't proceed
          if (!paymentResult) {
            return;
          }
        } catch {
          return;
        }
      }
    } catch {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/simple/price?ids=${coinId}&vs_currencies=usd`);
      const priceData = await response.json();
      const currentPrice = priceData[coinId]?.usd || 0;

      const coin = cryptoData.find(c => c.id === coinId);
      if (!coin) return;

      const currentItems = portfolio;
      const existingIndex = currentItems.findIndex(item => item.id === coinId);
      
      let updatedItems: PortfolioItem[];
      if (existingIndex >= 0) {
        const existing = currentItems[existingIndex];
        const totalQuantity = existing.quantity + quantity;
        const avgPrice = purchasePrice 
          ? ((existing.quantity * (existing.purchasePrice || existing.currentPrice)) + (quantity * purchasePrice)) / totalQuantity
          : existing.purchasePrice || existing.currentPrice;
        
        updatedItems = [...currentItems];
        updatedItems[existingIndex] = {
          ...existing,
          quantity: totalQuantity,
          purchasePrice: avgPrice,
          currentPrice: currentPrice
        };
      } else {
        updatedItems = [...currentItems, {
          id: coinId,
          symbol: coin.symbol,
          name: coin.name,
          quantity: quantity,
          purchasePrice: purchasePrice || currentPrice,
          currentPrice: currentPrice
        }];
      }

      updatePortfolioItems(currentPortfolioId, updatedItems);
    } catch (error) {
      console.error('Error adding coin:', error);
    }
  }, [portfolio, currentPortfolioId, updatePortfolioItems]);

  const removeCoin = useCallback((index: number) => {
    if (confirm('Remove this coin from your portfolio?')) {
      const updatedItems = portfolio.filter((_, i) => i !== index);
      updatePortfolioItems(currentPortfolioId, updatedItems);
    }
  }, [portfolio, currentPortfolioId, updatePortfolioItems]);

  return { 
    portfolio, 
    portfolios,
    currentPortfolioId,
    addCoin, 
    removeCoin, 
    refreshPortfolio,
    createPortfolio,
    deletePortfolio,
    renamePortfolio,
    switchPortfolio
  };
}
